import ForumLogoText from '@public/images/ForumLogoText.svg'
import Logo from '@public/images/ForummLogo.svg'
import { Button } from '@components/inputs/Button'
import { Form } from '@components/inputs/Form'
import { TextInput } from '@components/inputs/TextInput'
import React, { useEffect, useLayoutEffect, useState } from 'react'
import { useAuth } from '@libs/Auth/useAuth'
import Link from 'next/link'
import Box from '@components/base/Box'
import { useRouter } from 'next/router'
import { useTheme } from '@libs/useTheme'
import useLocalStorage from '@libs/useLocalStorage'
import { FaLinkedinIn } from 'react-icons/fa'
import { FcGoogle } from 'react-icons/fc'
import {
  FunnelIntent,
  parseFunnelIntent,
  pushFunnelLoginEvent,
} from '@components/base/GoogleTags'

const LoginForm = () => {
  const { theme, setTheme } = useTheme()
  const {
    signInWithEmail,
    signInWithGoogle,
    signInWithLinkedIn,
    getHostedAuthenticatedUser,
    currentUser,
  } = useAuth()
  const [loading, setLoading] = useState(false)
  const [isMounted, setIsMounted] = useState(false)
  const [password, setPassword] = useState('')

  const [isFirstLogin, setFirstLoginFlag] = useLocalStorage(
    'Forumm_FirstLogin',
    false
  )

  const [redirectTarget, setRedirectTarget] = useLocalStorage<
    | {
      path?: string
      analyticsIntent?: FunnelIntent
      event?: {
        eventId: string
        title: string
      }
    }
    | undefined
  >('Forumm_redirect_target', undefined)

  const router = useRouter()
  const queryIntent = parseFunnelIntent(router.query?.intent)
  const funnelIntent = queryIntent ?? redirectTarget?.analyticsIntent
  const requestedRedirect =
    typeof router.query?.redirect === 'string' ? router.query.redirect : ''
  const safeRedirect =
    requestedRedirect.startsWith('/') && !requestedRedirect.startsWith('//')
      ? requestedRedirect
      : ''

  const getRedirectPath = () =>
    redirectTarget?.path ||
    safeRedirect ||
    (funnelIntent === 'create_event' ? '/create-event' : '')

  const redirectAfterLogin = () => {
    const targetPath = getRedirectPath()

    if (targetPath) {
      setRedirectTarget(undefined)
      router.replace(targetPath)
      return
    }
    if (
      router.query?.previous &&
      !document.referrer.includes('www.forumm.to') &&
      !document.referrer.includes('//forumm.to')
    ) {
      router.back()
      return
    }

    router.replace(isFirstLogin ? '/create-event' : '/')
  }

  const beginHostedSignIn = async (signIn: () => Promise<unknown>) => {
    const targetPath = getRedirectPath()

    if (targetPath || funnelIntent) {
      setRedirectTarget({
        ...redirectTarget,
        ...(targetPath ? { path: targetPath } : {}),
        ...(funnelIntent ? { analyticsIntent: funnelIntent } : {}),
      })
    }

    await signIn()
  }

  useEffect(() => {
    if (currentUser) {
      redirectAfterLogin()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser])

  useEffect(() => {
    if (!router.isReady || currentUser) return

    getHostedAuthenticatedUser()
      .then(() => {
        redirectAfterLogin()
      })
      .catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.isReady, currentUser])

  useLayoutEffect(() => {
    if (router.isReady) setIsMounted(true)
  }, [router.isReady])

  const handlePasswordChange = (value: string, isInvalid?: boolean) => {
    setPassword(value)
  }

  if (!isMounted) return <></>

  return (
    <Box className="flex w-full justify-center h-screen items-center">
      <Box className="flex w-full justify-center items-center max-w-7xl flex-grow overflow-visible">
        <Box className="w-4/5 sm:w-1/2 my-16 relative">
          <Box className="flex flex-col items-center space-y-2">
            <Box
              onClick={() => (window.location.href = 'https://www.forumm.to')}
              className="text-center text-2xl cursor-pointer"
            >
              <div className="flex justify-center items-center mb-12">
                <Logo className="block w-12 mr-2" />
                <ForumLogoText fill={theme.textColour} className="h-6 w-24" />
              </div>
              <p>Log in to continue</p>
            </Box>
          </Box>
          <Box>
            <Box className="mt-8 mb-2">
              <Box className="mb-4 grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => beginHostedSignIn(signInWithGoogle)}
                  className="flex h-9 items-center justify-center gap-2 rounded border border-slate-400 bg-white px-3 text-xs font-medium text-slate-800 transition-colors hover:bg-slate-50"
                >
                  <FcGoogle className="h-4 w-4" />
                  <span>Google</span>
                </button>
                <button
                  type="button"
                  onClick={() => beginHostedSignIn(signInWithLinkedIn)}
                  className="flex h-9 items-center justify-center gap-2 rounded border border-slate-400 bg-white px-3 text-xs font-medium text-slate-800 transition-colors hover:bg-slate-50"
                >
                  <FaLinkedinIn className="h-4 w-4 text-[#0A66C2]" />
                  <span>LinkedIn</span>
                </button>
              </Box>
              <Box className="flex items-center gap-3 text-xs text-gray-500">
                <Box className="h-px flex-1 bg-gray-300" />
                <span>Quick log in</span>
                <Box className="h-px flex-1 bg-gray-300" />
              </Box>
            </Box>
            <Form
              initialFormData={{
                email:
                  decodeURIComponent((router?.query?.email as string) ?? '') ??
                  undefined,
              }}
              onSubmit={async (data) => {
                setLoading(true)
                try {
                  const { email, password } = data
                  await signInWithEmail(email, password)
                  pushFunnelLoginEvent(funnelIntent)
                  redirectAfterLogin()
                } catch (error: any) {
                  console.error(error)
                  setLoading(false)
                  return error.message ?? 'Failed to sign in'
                }
              }}
            >
              <TextInput
                label="Email"
                type="email"
                placeholder="your@mail.com"
                required
                validations={{
                  minLength: {
                    value: 7,
                    message: 'Email must be at least 7 characters',
                  },
                  maxLength: {
                    value: 70,
                    message: 'Email must be less than 70 characters',
                  },
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Email must be valid',
                  },
                }}
              />
              <div className="relative">
                <TextInput
                  type="password"
                  label="Password"
                  placeholder="*******"
                  required
                  value={password}
                  onChange={handlePasswordChange}
                  validations={'none'}
                />
              </div>
              <Button
                buttonType="submit"
                className="my-8 w-full"
                title="Log in"
                loading={loading}
                backgroundColor="#3763e9"
                textColor="white"
              />
            </Form>
          </Box>
          <Box className="text-sm flex space-x-2 justify-center mb-2 mt-2">
            <span className="">{"Don't have an Account?"}</span>
            <Link
              className="text-link hover:underline"
              href={{
                pathname: '/create-account',
                query: {
                  ...(funnelIntent ? { intent: funnelIntent } : {}),
                  ...(safeRedirect ? { redirect: safeRedirect } : {}),
                },
              }}
            >
              Sign up
            </Link>
          </Box>
          <Box className="text-sm flex space-x-2 justify-center mb-8">
            <span className="">Forgotten Password?</span>
            <Link className="text-link hover:underline" href="/forgot-password">
              Reset
            </Link>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

export default LoginForm
